import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { useLoadScript } from "@react-google-maps/api";
import { assets } from "../../Assets/assets";
import axios from "../../Utils/axiosInstance";
import { useAuth } from "../Context/AuthContext";
import ESignatureStep from "./Component/ESignatureStep";
import OtpStep from "./Component/OtpStep";
import PhoneStep from "./Component/PhoneStep";
import RegisterAddressStep from "./Component/RegisterAddressStep";
import RegisterDetailsStep from "./Component/RegisterDetailsStep";
import SelectRoleStep from "./Component/SelectRoleStep";

const STEP_HEADINGS = {
  phone: "Log in for a personalized experience",
  otp: "Verify OTP",
  "select-role": "Choose Your Account",
  "register-details": "Complete your profile",
  "register-address": "Add your locations (max 2)",
};

/** Steps on which we hide the decorative logo inside the right panel. */
const STEPS_WITH_COMPACT_LOGO = new Set(["phone", "otp"]);

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, setAuthData } = useAuth();
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ["places"],
  });

  // ── Core state ─────────────────────────────────────────────────────────────
  const initialRole = searchParams.get("role") === "supplier" ? "supplier" : "buyer";
  const [selectedRole, setSelectedRole] = useState(initialRole); // "buyer" | "supplier"
  const [phone, setPhone] = useState("");
  const [agreed, setAgreed] = useState(true);

  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [otpResendTimer, setOtpResendTimer] = useState(0);

  const [step, setStep] = useState("phone");

  useEffect(() => {
    const roleParam = searchParams.get("role");
    if (roleParam === "supplier" || roleParam === "buyer") {
      setSelectedRole(roleParam);
    }
  }, [searchParams]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // ── Registration state ─────────────────────────────────────────────────────
  const [regData, setRegData] = useState({
    firstName: "",
    middleName: "",
    lastName: "",
    email: "",
    GST: "",
    companyName: "",
    designation: "",
    IEC: "",
    businessType: "",
    referralCode: "",
  });

  const [hasGst, setHasGst] = useState("YES"); // "YES" | "NO" (for supplier)
  const [aadhaarNumber, setAadhaarNumber] = useState("");
  const [isAadharVerified, setIsAadharVerified] = useState(false);
  const [aadhaarDetails, setAadhaarDetails] = useState(null);

  const [gstInfo, setGstInfo] = useState(null);
  const [gstError, setGstError] = useState("");
  const [gstLoading, setGstLoading] = useState(false);

  const [countryCode, setCountryCode] = useState("+91");
  const [signature, setSignature] = useState(null);
  const [addresses, setAddresses] = useState([
    {
      addressType: "Office",
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "",
      pincode: "",
      country: "India",
      lat: null,
      lng: null,
    },
  ]);

  const [loadingAction, setLoadingAction] = useState("");
  // "get-otp" | "verify-otp" | "select-role-<role>" | "register" | ""

  // ── OTP timer ──────────────────────────────────────────────────────────────
  const startResendTimer = () => {
    setOtpResendTimer(30);
    const interval = setInterval(() => {
      setOtpResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // ── Send OTP ───────────────────────────────────────────────────────────────
  const handleGetOtp = async () => {
    if (phone.length !== 10) return toast.error("Enter 10-digit mobile number");
    if (!agreed) return toast.error("Please agree to terms & conditions");

    setLoadingAction("get-otp");

    try {
      const res = await axios.post(`/otp/send-otp?isLogin=true`, {
        identifier: phone,
        isLogin: true,
      });
      if (res.data.success) {
        toast.success("OTP sent successfully");
        setStep("otp");
        startResendTimer();
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to send OTP");
    } finally {
      setLoadingAction("");
    }
  };

  // ── Resend OTP ─────────────────────────────────────────────────────────────
  const handleResendOtp = () => {
    if (otpResendTimer > 0) return;
    handleGetOtp();
  };

  // ── Verify OTP ─────────────────────────────────────────────────────────────
  const handleVerifyOtp = async () => {
    const otpValue = otp.join("");
    if (otpValue.length !== 6) return toast.error("Enter complete 6-digit OTP");

    setLoadingAction("verify-otp");

    try {
      const res = await axios.post(`/user/login`, {
        identifier: phone,
        otp: otpValue,
        role: selectedRole,
      });

      const data = res.data;

      if (data.success && data.data?.token) {
        setAuthData(data.data.token, data.data.user);
        toast.success(`Logged in as ${selectedRole}`);
        navigate("/", { replace: true });
        return;
      }

      if (data.action === "Select_Role") {
        setStep("select-role");
        window.tempAccounts = data.data.accounts;
        toast("Multiple accounts found. Please select a role.", {
          icon: "ℹ️",
        });
        return;
      }

      if (data.action === "Register_Now" || !data.success) {
        toast.success(`No ${selectedRole} account found. Let's create one!`);
        const partner = data.data?.partnerUser;
        if (partner) {
          setRegData({
            firstName: partner.firstName || "",
            middleName: partner.middleName || "",
            lastName: partner.lastName || "",
            email: partner.email || "",
            companyName: partner.companyName || "",
            designation: partner.designation || "",
            GST: partner.GST || "",
            referralCode: partner.referralCode || "",
            IEC: partner.IEC || "",
            businessType: partner.businessType || "",
            isPartnerAutofilled: true,
          });
          if (partner.isGSTavailable !== undefined) {
            setHasGst(partner.isGSTavailable ? "YES" : "NO");
          }
          if (partner.aadhaarNumber) {
            setAadhaarNumber(partner.aadhaarNumber);
          }
          if (partner.e_signature) {
            setSignature(partner.e_signature);
          }
        }
        setStep("register-details");
        return;
      }

      toast.error(data.message || "Something went wrong");
    } catch (err) {
      const errorData = err?.response?.data;
      const msg = errorData?.message || "OTP verification failed";
      if (
        err?.response?.status === 404 ||
        errorData?.action === "Register_Now" ||
        msg.includes("does not exist") ||
        msg.includes("No account found")
      ) {
        toast.success(`No ${selectedRole} account found. Let's create one!`);
        const partner = errorData?.data?.partnerUser;
        if (partner) {
          setRegData({
            firstName: partner.firstName || "",
            middleName: partner.middleName || "",
            lastName: partner.lastName || "",
            email: partner.email || "",
            companyName: partner.companyName || "",
            designation: partner.designation || "",
            GST: partner.GST || "",
            referralCode: partner.referralCode || "",
            IEC: partner.IEC || "",
            businessType: partner.businessType || "",
            isPartnerAutofilled: true,
          });
          if (partner.isGSTavailable !== undefined) {
            setHasGst(partner.isGSTavailable ? "YES" : "NO");
          }
          if (partner.aadhaarNumber) {
            setAadhaarNumber(partner.aadhaarNumber);
          }
          if (partner.e_signature) {
            setSignature(partner.e_signature);
          }
        }
        setStep("register-details");
      } else {
        toast.error(msg);
      }
    } finally {
      setLoadingAction("");
    }
  };

  // ── Handle Role Selection ──────────────────────────────────────────────────
  const handleSelectRole = async (roleChoice) => {
    setLoadingAction(`select-role-${roleChoice}`);

    try {
      const res = await axios.post(`/user/login`, {
        identifier: phone,
        otp: otp.join(""),
        role: roleChoice,
      });

      if (res.data.success && res.data.data?.token) {
        setAuthData(res.data.data.token, res.data.data.user);
        toast.success(`Logged in as ${roleChoice}`);
        navigate("/", { replace: true });
      } else {
        toast.error(res.data.message || "Login failed");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to login with selected role",
      );
    } finally {
      setLoadingAction("");
    }
  };

  // ── GST Verification ───────────────────────────────────────────────────────
  const verifyGST = async (gstin) => {
    if (gstin.length !== 15) {
      setGstInfo(null);
      setGstError("");
      return;
    }
    setGstLoading(true);
    setGstError("");
    try {
      const url = `user/verify-gst`;
      const res = await axios.post(url, { gstNumber: gstin.toUpperCase() });
      const response = res.data;
      const rawGst = response?.data;
      const normalizedGst = rawGst
        ? {
          ...rawGst,
          GSTIN:
            rawGst.GSTIN ||
            rawGst.gstNumber ||
            rawGst.details?.GSTIN ||
            rawGst.details?.gstNumber ||
            gstin.toUpperCase(),
          gst_in_status:
            rawGst.gst_in_status || rawGst.details?.gst_in_status,
          legal_name_of_business:
            rawGst.legal_name_of_business ||
            rawGst.details?.legal_name_of_business,
          trade_name_of_business:
            rawGst.trade_name_of_business ||
            rawGst.details?.trade_name_of_business,
          taxpayer_type:
            rawGst.taxpayer_type || rawGst.details?.taxpayer_type,
          constitution_of_business:
            rawGst.constitution_of_business ||
            rawGst.details?.constitution_of_business,
          date_of_registration:
            rawGst.date_of_registration ||
            rawGst.details?.date_of_registration,
          last_update_date:
            rawGst.last_update_date || rawGst.details?.last_update_date,
          center_jurisdiction:
            rawGst.center_jurisdiction ||
            rawGst.details?.center_jurisdiction,
          state_jurisdiction:
            rawGst.state_jurisdiction || rawGst.details?.state_jurisdiction,
          principal_place_address:
            rawGst.principal_place_address ||
            rawGst.details?.principal_place_address,
          principal_place_split_address:
            rawGst.principal_place_split_address ||
            rawGst.details?.principal_place_split_address,
          nature_of_business_activities:
            rawGst.nature_of_business_activities ||
            rawGst.details?.nature_of_business_activities,
        }
        : null;

      if (
        response.success &&
        normalizedGst?.valid &&
        normalizedGst.gst_in_status === "Active"
      ) {
        setGstInfo(normalizedGst);

        // Autofill registration details
        setRegData((prev) => ({
          ...prev,
          GST: normalizedGst.GSTIN,
          companyName:
            normalizedGst.trade_name_of_business ||
            normalizedGst.legal_name_of_business ||
            "",
          businessType: normalizedGst.constitution_of_business || "",
        }));

        setGstError("");
        toast.success("GST verified successfully!");
      } else {
        setGstInfo(null);
        setGstError(
          response.data?.message ||
          response.message ||
          "Invalid or inactive GSTIN",
        );
      }
    } catch (err) {
      setGstInfo(null);
      setGstError(
        err?.response?.data?.message || "GST verification service error",
      );
    } finally {
      setGstLoading(false);
    }
  };

  const canSkipGstVerification = Boolean(
    regData.isPartnerAutofilled &&
    regData.GST &&
    String(regData.GST).trim().length === 15,
  );

  // ── Validation for Register Details Continue ───────────────────────────────
  const handleContinueFromDetails = () => {
    if (!regData.companyName) {
      toast.error("Please enter company name");
      return;
    }
    if (!regData.businessType) {
      toast.error("Please select business type");
      return;
    }
    if (!regData.designation) {
      toast.error("Please enter designation");
      return;
    }
    if (!regData.firstName || !regData.lastName || !regData.email) {
      toast.error("Please fill First Name, Last Name and Email");
      return;
    }

    if (selectedRole === "buyer" || hasGst === "YES") {
      if (!regData.GST || regData.GST.length !== 15) {
        toast.error("Please enter a valid 15-digit GST number");
        return;
      }
      if (gstLoading) {
        toast.error("GST verification in progress, please wait");
        return;
      }
      if (!gstInfo && !canSkipGstVerification) {
        toast.error("Please wait for GST verification to complete");
        return;
      }
    } else {
      // Supplier with hasGst === "NO"
      const cleanAadhaar = String(aadhaarNumber || "").replace(/\D/g, "");
      if (cleanAadhaar.length !== 12) {
        toast.error("Please enter a valid 12-digit Aadhaar number");
        return;
      }
      if (!isAadharVerified) {
        toast.error("Please verify your Aadhaar Card number via DigiLocker");
        return;
      }
    }

    setStep("e-signature");
  };

  // ── Final Registration ─────────────────────────────────────────────────────
  const handleCompleteRegistration = async () => {
    if (!regData.firstName || !regData.lastName || !regData.email) {
      return toast.error("Please fill First Name, Last Name and Email");
    }

    setLoadingAction("register");

    try {
      const isSupplierNoGst = selectedRole === "supplier" && hasGst === "NO";

      const payload = {
        phone,
        countryCode,
        e_signature: signature,
        firstName: regData.firstName,
        lastName: regData.lastName,
        email: regData.email || undefined,
        referralCode: regData.referralCode || undefined,
        role: selectedRole,
        isGSTavailable: selectedRole === "buyer" ? true : hasGst === "YES",
        GST:
          !isSupplierNoGst && regData.GST
            ? regData.GST.toUpperCase()
            : undefined,
        aadhaarNumber:
          isSupplierNoGst && aadhaarNumber
            ? String(aadhaarNumber).trim()
            : undefined,
        middleName: regData.middleName || undefined,
        companyName: regData.companyName,
        designation: regData.designation || undefined,
        IEC: regData.IEC ? regData.IEC.toUpperCase() : undefined,
        businessType: regData.businessType || undefined,
        identityVerification:
          isSupplierNoGst && aadhaarDetails
            ? {
              aadhaar: {
                verified: true,
                verifiedAt: new Date(),
                ...aadhaarDetails,
              },
            }
            : gstInfo
              ? {
                gst: {
                  verified: true,
                  verifiedAt: new Date(),
                  gstNumber: gstInfo.GSTIN,
                  legalBusinessName: gstInfo.legal_name_of_business,
                  tradeBusinessName: gstInfo.trade_name_of_business,
                  gstStatus: gstInfo.gst_in_status,
                  principalAddress: gstInfo.principal_place_address,
                },
              }
              : undefined,
      };

      const res = await axios.post(`/user/register`, payload);

      if (res.data.success && res.data.data?.token) {
        setAuthData(res.data.data.token, res.data.data.user);

        // ── Address handling ───────────────────────────────────────────────
        const validAddresses = addresses.filter(
          (addr) =>
            addr.addressLine1 && addr.city && addr.state && addr.pincode,
        );

        if (validAddresses.length > 0) {
          for (let i = 0; i < validAddresses.length; i++) {
            await axios.post(`/address`, {
              ...validAddresses[i],
              isDefault: i === 0,
            });
          }
          toast.success("Registration successful with addresses!");
        } else {
          toast.success(
            "Registration successful! You can add addresses later from your profile.",
          );
        }

        navigate("/", { replace: true });
      } else {
        toast.error(res.data.message || "Registration failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Registration failed");
    } finally {
      setLoadingAction("");
    }
  };

  return (
    <div className="min-h-screen bg-[#FFECF1] flex items-center justify-center md:px-4">
      {/* ── MOBILE LAYOUT (shown only on small screens) ────────────────────── */}
      <div className="md:hidden w-full min-h-screen bg-white flex flex-col">
        {/* Mobile top header bar */}
        <div className="bg-primaryColor px-4 py-4 relative overflow-hidden">
          {/* Decorative circles */}
          <div className="absolute -top-6 -right-6 w-28 h-28 rounded-full bg-white/10" />
          <div className="absolute top-2 -right-2 w-14 h-14 rounded-full bg-white/10" />
          <div className="absolute -bottom-4 -left-4 w-20 h-20 rounded-full bg-white/10" />

          {/* Skip button (for register steps) */}
          {step === "register-address" && (
            <button
              type="button"
              onClick={handleCompleteRegistration}
              className="absolute top-4 z-20 right-4 text-white/80 text-sm font-medium"
            >
              Skip
            </button>
          )}

          {/* Logo */}
          <div className="flex justify-center relative z-10">
            <img
              title="Back to home"
              onClick={() => navigate("/")}
              src={assets.qqtdark}
              alt="logo"
              className="w-24 cursor-pointer object-contain"
            />
          </div>
        </div>

        {/* White card below header */}
        <div className="flex-1 -mt-5 bg-white rounded-t-3xl px-5 pt-6 pb-6 shadow-lg flex flex-col overflow-hidden">
          {/* ── PHONE STEP ──────────────────────────────────────────────── */}
          {step === "phone" && (
            <PhoneStep
              phone={phone}
              setPhone={setPhone}
              agreed={agreed}
              setAgreed={setAgreed}
              loadingAction={loadingAction}
              onGetOtp={handleGetOtp}
              countryCode={countryCode}
              setCountryCode={setCountryCode}
              selectedRole={selectedRole}
              setSelectedRole={setSelectedRole}
            />
          )}

          {/* ── OTP STEP ────────────────────────────────────────────────── */}
          {step === "otp" && (
            <OtpStep
              phone={phone}
              otp={otp}
              setOtp={setOtp}
              otpResendTimer={otpResendTimer}
              loadingAction={loadingAction}
              onVerifyOtp={handleVerifyOtp}
              onResendOtp={handleResendOtp}
              onBack={() => setStep("phone")}
            />
          )}

          {/* ── SELECT ROLE STEP ─────────────────────────────────────────── */}
          {step === "select-role" && (
            <SelectRoleStep
              accounts={window.tempAccounts || []}
              loadingAction={loadingAction}
              onSelectRole={handleSelectRole}
            />
          )}

          {/* ── REGISTER: PERSONAL DETAILS + GST / AADHAAR ───────────────── */}
          {step === "register-details" && (
            <RegisterDetailsStep
              regData={regData}
              setRegData={setRegData}
              selectedRole={selectedRole}
              hasGst={hasGst}
              setHasGst={setHasGst}
              aadhaarNumber={aadhaarNumber}
              setAadhaarNumber={setAadhaarNumber}
              isAadharVerified={isAadharVerified}
              setIsAadharVerified={setIsAadharVerified}
              aadhaarDetails={aadhaarDetails}
              setAadhaarDetails={setAadhaarDetails}
              gstInfo={gstInfo}
              gstError={gstError}
              gstLoading={gstLoading}
              onVerifyGST={verifyGST}
              onContinue={handleContinueFromDetails}
            />
          )}

          {step === "e-signature" && (
            <ESignatureStep
              signature={signature}
              setSignature={setSignature}
              isAutofilled={!!regData.isPartnerAutofilled && !!signature}
              onBack={() => setStep("register-details")}
              onContinue={() => setStep("register-address")}
            />
          )}

          {/* ── REGISTER: ADDRESSES ──────────────────────────────────────── */}
          {step === "register-address" && (
            <RegisterAddressStep
              addresses={addresses}
              setAddresses={setAddresses}
              loadingAction={loadingAction}
              onFinish={handleCompleteRegistration}
              onBack={() => setStep("register-details")}
              isMapLoaded={isLoaded}
            />
          )}
        </div>
      </div>

      {/* ── DESKTOP LAYOUT (shown only on md+) ────────────────────────────── */}
      <div className="hidden md:flex w-full 2xl:max-w-7xl max-w-6xl min-h-[90vh] max-h-[90vh] bg-white rounded-2xl shadow-lg overflow-hidden flex-row">
        {/* ── LEFT BANNER ────────────────────────────────────────────────────── */}
        <div
          style={{ backgroundImage: `url(${assets.newloginbanner})` }}
          className="hidden md:flex md:w-1/2 relative bg-primaryColor items-center bg-cover bg-center bg-no-repeat justify-center"
        ></div>

        {/* ── RIGHT FORM AREA ──────────────────────────────────────────────── */}
        <div className="md:w-1/2 w-full flex items-center justify-center px-6 py-5 overflow-y-auto">
          <div className="w-full max-w-md">
            <img
              title="Back to home"
              onClick={() => navigate("/")}
              src={assets.qqtlogo}
              alt="logo"
              className={`w-32 mb-5 cursor-pointer mx-auto md:mx-0
                ${STEPS_WITH_COMPACT_LOGO.has(step) ? "block" : "hidden md:hidden"}
              `}
            />

            {/* ── PHONE STEP ──────────────────────────────────────────────── */}
            {step === "phone" && (
              <PhoneStep
                phone={phone}
                setPhone={setPhone}
                agreed={agreed}
                setAgreed={setAgreed}
                loadingAction={loadingAction}
                onGetOtp={handleGetOtp}
                countryCode={countryCode}
                setCountryCode={setCountryCode}
                selectedRole={selectedRole}
                setSelectedRole={setSelectedRole}
              />
            )}

            {/* ── OTP STEP ────────────────────────────────────────────────── */}
            {step === "otp" && (
              <OtpStep
                phone={phone}
                otp={otp}
                setOtp={setOtp}
                otpResendTimer={otpResendTimer}
                loadingAction={loadingAction}
                onVerifyOtp={handleVerifyOtp}
                onResendOtp={handleResendOtp}
                onBack={() => setStep("phone")}
              />
            )}

            {/* ── SELECT ROLE STEP ─────────────────────────────────────────── */}
            {step === "select-role" && (
              <SelectRoleStep
                accounts={window.tempAccounts || []}
                loadingAction={loadingAction}
                onSelectRole={handleSelectRole}
              />
            )}

            {/* ── REGISTER: PERSONAL DETAILS + GST / AADHAAR ───────────────── */}
            {step === "register-details" && (
              <RegisterDetailsStep
                regData={regData}
                setRegData={setRegData}
                selectedRole={selectedRole}
                hasGst={hasGst}
                setHasGst={setHasGst}
                aadhaarNumber={aadhaarNumber}
                setAadhaarNumber={setAadhaarNumber}
                isAadharVerified={isAadharVerified}
                setIsAadharVerified={setIsAadharVerified}
                aadhaarDetails={aadhaarDetails}
                setAadhaarDetails={setAadhaarDetails}
                gstInfo={gstInfo}
                gstError={gstError}
                gstLoading={gstLoading}
                onVerifyGST={verifyGST}
                onContinue={handleContinueFromDetails}
              />
            )}

            {step === "e-signature" && (
              <ESignatureStep
                signature={signature}
                setSignature={setSignature}
                isAutofilled={!!regData.isPartnerAutofilled && !!signature}
                onBack={() => setStep("register-details")}
                onContinue={() => setStep("register-address")}
              />
            )}

            {/* ── REGISTER: ADDRESSES ──────────────────────────────────────── */}
            {step === "register-address" && (
              <RegisterAddressStep
                addresses={addresses}
                setAddresses={setAddresses}
                loadingAction={loadingAction}
                onFinish={handleCompleteRegistration}
                onBack={() => setStep("register-details")}
                isMapLoaded={isLoaded}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
